import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useLocalization } from "@/hooks/useLocalization";

interface OrderSummary {
  id: string;
  order_id: string;
  thumb: string;
  status: string;
  unpaid: number;
  updated: string;
  created: string;
  isActive: boolean;
  product?: {
    name: string;
    color: string;
    brand: string;
  };
  eta?: string;
}

interface OrderCarouselProps {
  orders: OrderSummary[];
  selectedOrderId?: string;
  onOrderSelect: (orderId: string) => void;
  filter: 'active' | 'completed' | 'all';
  className?: string;
}

const getStatusColor = (status: string) => {
  switch (status) {
    case 'pending':
      return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
    case 'deposit':
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
    case 'production':
      return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200';
    case 'ready':
      return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
    default:
      return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
  }
};

const getStatusLabel = (status: string, t: (key: string) => string) => {
  switch (status) {
    case 'pending':
      return t('pending_caps');
    case 'deposit':
      return t('deposit_paid_caps');
    case 'production':
      return 'In Production';
    case 'ready':
      return 'Ready';
    default:
      return status;
  }
};

export function OrderCarousel({ orders, selectedOrderId, onOrderSelect, filter, className }: OrderCarouselProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isDesktop, setIsDesktop] = useState(false);
  const { t } = useLocalization();

  // Filter orders based on filter type
  const filteredOrders = orders.filter(order => {
    if (filter === 'active') return order.isActive;
    if (filter === 'completed') return !order.isActive;
    return true;
  });

  // Check if we're on desktop
  useEffect(() => {
    const checkDesktop = () => {
      setIsDesktop(window.innerWidth >= 768);
    };
    
    checkDesktop();
    window.addEventListener('resize', checkDesktop);
    return () => window.removeEventListener('resize', checkDesktop);
  }, []);

  // Update current index when selected order changes
  useEffect(() => {
    if (selectedOrderId) {
      const index = filteredOrders.findIndex(order => order.id === selectedOrderId);
      if (index !== -1) {
        setCurrentIndex(index);
      }
    }
  }, [selectedOrderId, filteredOrders]);

  // Auto-select first active order or most recent
  useEffect(() => {
    if (filteredOrders.length > 0 && !selectedOrderId) {
      const activeOrder = filteredOrders.find(order => order.isActive);
      const targetOrder = activeOrder || filteredOrders[0];
      onOrderSelect(targetOrder.id);
    }
  }, [filteredOrders, selectedOrderId, onOrderSelect]);

  const handlePrevious = () => {
    if (currentIndex > 0) {
      const newIndex = currentIndex - 1;
      setCurrentIndex(newIndex);
      onOrderSelect(filteredOrders[newIndex].id);
    }
  };

  const handleNext = () => {
    if (currentIndex < filteredOrders.length - 1) {
      const newIndex = currentIndex + 1;
      setCurrentIndex(newIndex);
      onOrderSelect(filteredOrders[newIndex].id);
    }
  };

  const handleOrderClick = (order: OrderSummary, index: number) => {
    setCurrentIndex(index);
    onOrderSelect(order.id);
  };

  if (filteredOrders.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-muted-foreground">{t('no_completed_orders_found_caps')}</p>
      </div>
    );
  }

  // Desktop List View
  if (isDesktop) {
    return (
      <div className={cn("space-y-2", className)}>
        {filteredOrders.map((order, index) => (
          <Card
            key={order.id}
            className={cn(
              "cursor-pointer transition-all duration-200 hover:shadow-md",
              selectedOrderId === order.id && "ring-2 ring-primary",
              "border-l-4",
              order.status === 'ready' ? "border-l-green-500" :
              order.status === 'production' ? "border-l-purple-500" :
              order.status === 'deposit' ? "border-l-blue-500" : "border-l-yellow-500"
            )}
            onClick={() => handleOrderClick(order, index)}
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-4">
                <img
                  src={order.thumb}
                  alt={order.product?.name || "Order"}
                  className="w-16 h-16 object-cover rounded-md"
                />
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-semibold">Order {order.id}</h3>
                    <Badge className={getStatusColor(order.status)}>
                      {getStatusLabel(order.status, t)}
                    </Badge>
                    {order.unpaid > 0 && (
                      <Badge variant="destructive" className="text-xs">
                        {order.unpaid} unpaid
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {order.product?.name} • {order.product?.color}
                    {order.product?.brand && ` • ${order.product.brand}`}
                  </p>
                </div>
                <div className="text-right text-sm text-muted-foreground">
                  Updated {new Date(order.updated).toLocaleDateString()}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  // Mobile Carousel View
  return (
    <div className={cn("space-y-4", className)}>
      <div className="relative">
        {/* Navigation Arrows */}
        {filteredOrders.length > 1 && (
          <>
            <button
              onClick={handlePrevious}
              disabled={currentIndex === 0}
              className="absolute left-2 top-1/2 -translate-y-1/2 z-10 p-2 rounded-full bg-white/80 shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Previous order"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              onClick={handleNext}
              disabled={currentIndex === filteredOrders.length - 1}
              className="absolute right-2 top-1/2 -translate-y-1/2 z-10 p-2 rounded-full bg-white/80 shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Next order"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </>
        )}

        {/* Current Order Card */}
        <Card className="mx-8">
          <CardContent className="p-4">
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <img
                  src={filteredOrders[currentIndex].thumb}
                  alt={filteredOrders[currentIndex].product?.name || "Order"}
                  className="w-20 h-20 object-cover rounded-md"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-semibold truncate">Order {filteredOrders[currentIndex].id}</h3>
                    {filteredOrders[currentIndex].unpaid > 0 && (
                      <Badge variant="destructive" className="text-xs shrink-0">
                        {filteredOrders[currentIndex].unpaid}
                      </Badge>
                    )}
                  </div>
                  <Badge className={cn("mb-2", getStatusColor(filteredOrders[currentIndex].status))}>
                    {getStatusLabel(filteredOrders[currentIndex].status, t)}
                  </Badge>
                  <p className="text-sm text-muted-foreground">
                    {filteredOrders[currentIndex].product?.name}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {filteredOrders[currentIndex].product?.color}
                    {filteredOrders[currentIndex].product?.brand && 
                      ` • ${filteredOrders[currentIndex].product.brand}`
                    }
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Pagination Dots */}
      {filteredOrders.length > 1 && (
        <div className="flex justify-center gap-2">
          {filteredOrders.map((_, index) => (
            <button
              key={index}
              onClick={() => handleOrderClick(filteredOrders[index], index)}
              className={cn(
                "w-2 h-2 rounded-full transition-colors",
                index === currentIndex ? "bg-primary" : "bg-muted"
              )}
              aria-label={`Go to order ${index + 1}`}
            />
          ))}
        </div>
      )}

      {/* Screen Reader Summary */}
      <div className="sr-only">
        Order {currentIndex + 1} of {filteredOrders.length}: 
        Order {filteredOrders[currentIndex].id}, 
        {filteredOrders[currentIndex].product?.name}, 
        {getStatusLabel(filteredOrders[currentIndex].status, t)}
        {filteredOrders[currentIndex].unpaid > 0 && 
          `, ${filteredOrders[currentIndex].unpaid} unpaid invoice${filteredOrders[currentIndex].unpaid > 1 ? 's' : ''}`
        }
      </div>
    </div>
  );
}